import $ from 'jquery';
import debounce from 'lodash/debounce.js';
import {initRevealAnimation} from '../../js/Ui.mjs';
import JobPostingState from './JobPostingStateManager.mjs';
import LoadingIndicator from '../../js/ui/LoadingIndicator.mjs';
import Alert from '../../js/modules/Alert.mjs';
import Autocomplete from './Autocomplete.mjs';
import GeoLocation from './GeoLocation.mjs';
import GoogleMapsLoader from './GoogleMapsLoader.mjs';
import {
  getTranslation,
  translations,
} from '../../js/utilities/translations.mjs';

const SELECTOR_FORM = '#job-postings-search-filter-form';
const SELECTOR_LIST_ITEM = '.ef-job-postings-list__list-item';
const SELECTOR_LIST_APPEND = '.ef-job-postings-list__list-append';
const SELECTOR_OFF_CANVAS_FILTER = '#off-canvas-menu-job-postings-filter';
const SELECTOR_FILTER_LOCATION = '#off-canvas-menu-job-postings-filter-location-input';
const SELECTOR_FILTER_LOCATION_ERROR = '#off-canvas-menu-job-postings-filter-location-error';
const $node = $('.ef-job-postings-list');
const $list = $node.find('.ef-job-postings-list__list');
const pageState = JSON.parse($('#job-postings-list-state').text()) || {};
const $emptyResultText = $node.find('.ef-text p');
const $emptyResultResetButton = $node.find('.ef-text .btn');
let $listAppend = $node.find(SELECTOR_LIST_APPEND);

init();

function init() {
  JobPostingState.subscribe(handleStateChange);

  if (JobPostingState.restoreState() === false) {
    initState();
  }

  setupEventListeners();

  // Move filter off-canvas outside the #content-canvas before the page-shadow
  const $filterOffCanvas = $(SELECTOR_OFF_CANVAS_FILTER);
  const $pageShadow = $('#page-shadow');
  $filterOffCanvas.insertBefore($pageShadow);

  initGeoLocationSearch();
}

function initState() {
  JobPostingState.init({
    ...pageState,
    href: window.location.href,
    $jobPostings: $list.find(SELECTOR_LIST_ITEM),
    $listAppend,
    $form: $(SELECTOR_FORM),
  });

  // Add JobPostingState to history state
  history.replaceState({jobState: JobPostingState.toSerialized()}, '');
}

function setupEventListeners() {
  // Restore JobPostingState from history state if available
  addEventListener('popstate', (event) => {
    if (event?.state?.jobState) {
      JobPostingState.restoreFromSerialized(event.state.jobState);
    }
  });
  // Restore JobPostingState if page is loaded from back/forward cache
  addEventListener('pageshow', async (event) => {
    if (event.persisted && JobPostingState?.state) {
      await handleStateChange(JobPostingState.state);
    }
  });

  // Reflect filter state in the URL and history when closing filter off-canvas
  $(SELECTOR_OFF_CANVAS_FILTER).on('closed.offcanvas', () => {
    const isInInitialState = JobPostingState.state.isInitial;
    const newUrl = getFormUrlWithParams($(SELECTOR_FORM));

    if (isInInitialState || newUrl.href === window.location.href) {
      // Nothing changed
      return;
    }

    history.pushState(
      {jobState: JobPostingState.toSerialized()},
      '',
      newUrl.href,
    );
  })

  // Item link
  $(document).on('click', `${SELECTOR_LIST_ITEM} a`, (event) => {
    const targetHref = $(event.currentTarget).attr('href');
    event.preventDefault();

    JobPostingState.update({
      $jobPostings: $(SELECTOR_LIST_ITEM),
    });

    location.href = targetHref;
  });

  // Show more button
  $(document).on('click', `${SELECTOR_LIST_APPEND} > a.btn`, (event) => {
    const $moreLink = $(event.currentTarget);

    event.preventDefault();

    // Only show loading indicator for manual loading and not for automatic
    LoadingIndicator.show();

    loadMoreJobPostings({$moreLink});
  });

  // Filter form
  const debouncedLoadFilteredJobPostings = debounce((params) => {
    // Prevent excessive requests in Firefox when holding ENTER on checkbox filters
    loadFilteredJobPostings(params);
  }, 500);
  $(document).on('submit', SELECTOR_FORM, (event) => {
    const $form = $(event.currentTarget);
    event.preventDefault();

    debouncedLoadFilteredJobPostings({$form, event: 'submit'});
  });
  $(document).on('change reset', SELECTOR_FORM, debounce((event) => {
    const $form = $(event.currentTarget);
    const eventType = event.type;

    loadFilteredJobPostings({$form, eventType});
  }, 500));

  // Reset button (empty result)
  $emptyResultResetButton.on('click', () => {
    // Reset the form
    $(SELECTOR_FORM)[0].reset();
  });
}

function loadMoreJobPostings({$moreLink}) {
  $.ajax({
    url: $moreLink.attr('href'),
    method: 'GET',
    dataType: 'html',
    success: (data) => {
      const $data = $(data);
      const $newJobPostings = $data.find(SELECTOR_LIST_ITEM);
      const $newListAppend = $data.find(SELECTOR_LIST_APPEND);
      const $newMoreLink = $newListAppend.find('a.btn');
      const newPageState = JSON.parse($data.find('#job-postings-list-state').text() || {});

      JobPostingState.addJobPostings({
        ...newPageState,
        $jobPostings: $newJobPostings,
        $listAppend: $newListAppend,
      })

      if ($newMoreLink.length === 0) {
        // Stop processing
        return;
      }

      initAutomaticLoading({
        $jobPostings: $newJobPostings,
        $moreLink: $newMoreLink,
      });
    },
    error: () => {
      LoadingIndicator.hide();
    },
  });

  function initAutomaticLoading({$jobPostings, $moreLink}) {
    const options = {
      threshold: 0,
    };
    const thirdLastJobPosting = $jobPostings.eq(-3).get(0);
    const Observer = new IntersectionObserver(
      (entries, observer) => handleIntersect(
        entries,
        observer,
        {$moreLink}),
      options,
    );

    // We observe the third-last job posting, which work for mobile and desktop equally
    Observer.observe(thirdLastJobPosting);
  }

  function handleIntersect(entries, Observer, {$moreLink}) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Stop observing the loaded job postings
        Observer.unobserve(entry.target);
        // Start loading more job postings
        loadMoreJobPostings({$moreLink});
      }
    });
  }
}

function loadFilteredJobPostings({$form, eventType}) {
  const url = $form.attr('action');
  $form.find('input[type="hidden"]').filter((index, input) => {
    // Filter tx_jobs_jobpostinglist[__*] inputs before sending
    return input.name.startsWith('tx_jobs_jobpostinglist[__');
  }).remove();
  const params = eventType === 'reset' ? {} : $form.serialize();
  const previousLocation = $(SELECTOR_FILTER_LOCATION).val();

  LoadingIndicator.show();

  $.ajax({
    url,
    method: 'GET',
    dataType: 'html',
    data: params,
    success: (data) => {
      const $data = $(data);
      const $newJobPostings = $data.find(SELECTOR_LIST_ITEM);
      const $newListAppend = $data.find(SELECTOR_LIST_APPEND);
      const $newForm = $data.find(SELECTOR_FORM);
      const newPageState = JSON.parse($data.find('#job-postings-list-state').text() || {});
      const newUrl = getFormUrlWithParams($newForm);

      JobPostingState.update({
        ...newPageState,
        href: newUrl.href,
        $jobPostings: $newJobPostings,
        $listAppend: $newListAppend,
        $form: $newForm,
        location: eventType === 'reset' ? '' : previousLocation,
      });
    },
    error: () => {
      LoadingIndicator.hide();
    },
  });
}

/**
 * @param {jQuery} $form
 * @returns {URL}
 */
function getFormUrlWithParams($form) {
  const newUrl = new URL($form.attr('action'), window.location.origin);
  const params = new URLSearchParams($form.serialize());
  for (const [key, value] of params) {
    if (key.startsWith('tx_jobs_jobpostinglist[__')) {
      // Filter tx_jobs_jobpostinglist[__*] parameters before sending
      continue;
    }
    if (value === '') {
      // Filter empty parameters
      continue;
    }
    newUrl.searchParams.append(key, value);
  }

  return newUrl;
}

async function handleStateChange(state) {
  const lastFocusedElement = document.activeElement;

  await Promise.all([
    updateList(state),
    updateCount(state),
    updateFilters(state),
    updateEmptyResultText(state),
  ]);

  LoadingIndicator.hide();

  if (state.isAppendMode) {
    // TODO: Currently disabled to avoid scroll jumps when automatically loading
    // Focus the first new job posting
    // const $firstNewJobPostingLink = state.$firstNewJobPosting.find('a');
    // restoreFocus({elementToFocus: $firstNewJobPostingLink.get(0)});
  } else {
    restoreFocus({lastFocusedElement});
  }
  initRevealAnimation($node.find('.reveal:not(.-revealed)'));
}

function updateList(state) {
  return new Promise((resolve) => {
    // Update list items
    $list.html(state.$jobPostings);

    // Update load more link
    if (state.$listAppend.length === 0) {
      // Remove when not necessary
      $listAppend.remove();
    } else if ($listAppend.length === 0) {
      // Re-add after f.e. filtering
      $list.after(state.$listAppend);
    } else {
      // Button exists and items available, update it
      $listAppend.replaceWith(state.$listAppend);
    }

    // Update the selector, since we might have replaced it
    $listAppend = state.$listAppend;

    resolve();
  });
}

function updateCount(state) {
  return new Promise((resolve) => {
    const count = state.totalItemsCount.toLocaleString(
      'de-DE',
      {useGrouping: true}
    );
    const $nodes = [
      // List headline
      $node.find('.headline'),
      // Filter off canvas title
      $(`${SELECTOR_OFF_CANVAS_FILTER} [data-off-canvas-title]`),
      // Filter off canvas close button
      $(`${SELECTOR_OFF_CANVAS_FILTER} .off-canvas__footer [data-off-canvas-close]`),
    ];

    $nodes.forEach(($node) => {
      $node.text(
        $node.text().replace(/\d+(?:\.\d+)?/, count)
      );
    });

    resolve();
  });
}

function updateFilters(state) {
  return new Promise((resolve) => {
    // Update filter toggle button
    const $filterBtn = $('.ef-job-postings-filter .btn .btn-label');
    const baseText = $filterBtn.text().replace(/\s*\(\d+\)$/, '');
    $filterBtn.text(state.activeFiltersCount > 0
      ? `${baseText} (${state.activeFiltersCount})`
      : baseText);

    // Update filter form
    const $newForm = $(state.$form);
    const $form = $(SELECTOR_FORM);
    $form.replaceWith($newForm);

    initGeoLocationSearch();

    // Update location input
    const $locationInput = $newForm.find(SELECTOR_FILTER_LOCATION);
    $locationInput.val(state.location);
    if (state.location !== '') {
      $locationInput.addClass('has-value');
    }

    resolve();
  });
}

function updateEmptyResultText(state) {
  return new Promise((resolve) => {
    if (state.totalItemsCount === 0) {
      $emptyResultText.attr('class', 'reveal');
      $emptyResultResetButton.removeClass('d-none').addClass('reveal');
    } else {
      $emptyResultText.attr('class', 'd-none');
      $emptyResultResetButton.removeClass('reveal -revealed').addClass('d-none');
    }

    resolve();
  });
}

function restoreFocus({lastFocusedElement, elementToFocus}) {
  // First try to find the element by ID (suitable for inputs)
  if (lastFocusedElement?.id) {
    elementToFocus = document.getElementById(lastFocusedElement.id);
  }

  // Fallback to text (suitable for buttons)
  if (!elementToFocus && lastFocusedElement?.textContent) {
    const lastFocusedElementText = lastFocusedElement.textContent.trim();
    elementToFocus = Array.from(document.querySelectorAll(lastFocusedElement.tagName))
    .find(el => el.textContent?.trim() === lastFocusedElementText);
  }

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

function initGeoLocationSearch() {
  const $form = $(SELECTOR_FORM);
  const $locationInput = $(SELECTOR_FILTER_LOCATION);
  const $clearLocationInputBtn = $('#off-canvas-menu-job-postings-filter-location-clear');
  const $coordinatesInput = $('#off-canvas-menu-job-postings-filter-location-coordinates');
  const $distanceInput = $('#off-canvas-menu-job-postings-filter-location-distance');
  let geocoderIsRunning = false;

  fillEmptyAddressInputFromCoordinates();

  $locationInput.on('change', (event) => {
    event.stopPropagation();

    if ($locationInput.val() === '') {
      $locationInput.removeClass('has-value');
      clearCoordinates();
    } else {
      $locationInput.addClass('has-value');
    }
  });
  $clearLocationInputBtn.on('click', () => {
    $locationInput.val('');
    $locationInput.removeClass('has-value');
    clearCoordinates();
  });
  $distanceInput.on('change', (event) => {
    event.stopPropagation();

    // Only submit form if coordinates are set
    if ($coordinatesInput.val() !== '') {
      $form.submit();
    }
  });

  function clearCoordinates() {
    $coordinatesInput.val('');
    $form.submit();
  }

  new Autocomplete(
    $locationInput,
    (name, coordinates) => {
      JobPostingState.update({
        location: name,
      })
      updateAndSubmitLocationSearch(name, coordinates);
    },
    (data) => {
      geocodeData(handleGeocoderResult, data);
    },
  );

  new GeoLocation(
    $('#off-canvas-menu-job-postings-filter-location-determine'),
    (data) => {
      removeAlert();
      geocodeData(handleGeocoderResult, data);
    },
    (error) => {
      LoadingIndicator.hide();
      addAlert(new Alert({
        'typeWarning': true,
        'iconId': 'exclamation',
        'copytext': error,
        'additionalClasses': 'mb-0',
      }));
    },
  );

  /**
   * Fill address input from coordinates if they exist and location input is empty
   */
  function fillEmptyAddressInputFromCoordinates() {
    if ($coordinatesInput.val() === '' || $locationInput.val() !== '') {
      return;
    }

    if (JobPostingState.state.location !== '') {
      setLocationAddress(JobPostingState.state.location);
      return;
    }

    const coordinates = $coordinatesInput.val().split(',');
    if (coordinates.length !== 2) {
      return;
    }

    const lat = parseFloat(coordinates[0]);
    const lng = parseFloat(coordinates[1]);
    if (isNaN(lat) || isNaN(lng)) {
      return;
    }

    geocodeCoordinates(lat, lng, (address) => {
      setLocationAddress(address)
      JobPostingState.update({
        location: address,
      });
    });
  }

  /**
   * Update input field and state without form submission
   * @param {string} address
   */
  function setLocationAddress(address) {
    $locationInput.val(address);
    $locationInput.addClass('has-value');
  }

  /**
   * Updates the location input field and coordinates input field with form submit
   * @param name
   * @param coordinates
   */
  function updateAndSubmitLocationSearch(name, coordinates) {
    $locationInput.val(name);
    $coordinatesInput.val(`${coordinates.lat()},${coordinates.lng()}`);
    $form.submit();
    LoadingIndicator.hide();
  }

  /**
   * Performs reverse geocoding for coordinates and updates the location input
   * field without submitting the form
   *
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @param {function} callbackGeocoderResult with address as argument
   */
  function geocodeCoordinates(lat, lng, callbackGeocoderResult) {
    GoogleMapsLoader.load().then((google) => {
      const geocoder = new google.maps.Geocoder();
      const latLng = new google.maps.LatLng(lat, lng);

      LoadingIndicator.show();

      geocoder.geocode({ 'location': latLng })
        .then(({ results }) => {
          if (results && results.length > 0) {
            const address = results[0].formatted_address;
            callbackGeocoderResult(address);
          }
          LoadingIndicator.hide();
        })
        .catch((error) => {
          console.error('Reverse geocoding failed:', error);
          LoadingIndicator.hide();
        });
    }).catch((exception) => {
      console.error('Error loading Google Maps:', exception);
      LoadingIndicator.hide();
    });
  }

  /**
   * Performs a geocoding request
   *
   * @param {function} callbackGeocoderResult
   * @param {google.maps.GeocoderRequest} data https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderRequest
   */
  function geocodeData(callbackGeocoderResult, data) {
    if (geocoderIsRunning) {
      // Don't run twice, f.e. within form submit and autocomplete
      // which could start asynchronously
      return;
    }

    GoogleMapsLoader.load().then((google) => {
      geocoderIsRunning = true;
      (new google.maps.Geocoder())
      .geocode(data)
      .then(callbackGeocoderResult.bind(this))
      .catch(() => {
        geocoderIsRunning = false;
        addAlert(new Alert({
          "typeDanger": true,
          "iconId": "exclamation",
          "copytext": getTranslation(translations.GeoLocation.ADDRESS_NOT_FOUND),
          'additionalClasses': 'mb-0',
        }));
      });
    }).catch((exception) => {
      geocoderIsRunning = false;
      LoadingIndicator.hide();
      console.log('geocodeData - Error GoogleMapsLoader:', exception);
    });
  }

  function handleGeocoderResult({results}) {
    if (results.length < 1) {
      $locationInput.val('');
      LoadingIndicator.hide();
      return;
    }

    // Get nearest address
    const name = results[0]['formatted_address'];
    const coordinates = results[0].geometry.location;
    updateAndSubmitLocationSearch(name, coordinates);

    geocoderIsRunning = false;
  }

  function addAlert($alert) {
    LoadingIndicator.hide();
    $(SELECTOR_FILTER_LOCATION_ERROR).html($alert);
  }

  function removeAlert() {
    $(SELECTOR_FILTER_LOCATION_ERROR).empty();
  }
}
